import React, { memo, ChangeEvent, useEffect, useState, useCallback } from 'react'
import { TextArea } from 'transfix-ui/components/TextArea'
import { useDebouncedCallback } from 'use-debounce'
import { IStop } from 'apps/fleet-planner/types/loads'
import { Button, Card, InlineEditContainer } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { DISPLAY } from 'constants/display'

interface InstructionDisplayProps {
  dataTest: string
  instructions: IStop['instructions']
}

export const InstructionDisplay = memo<InstructionDisplayProps>(({ dataTest, instructions }) => {
  return <span data-test={dataTest}>{instructions || DISPLAY.empty}</span>
})

interface IInstructionInlineEdit {
  stop: IStop
  currentEditField?: string
  onEditModeRequested(name?: string): void
  onSubmit: () => void
  onCancel: () => void
  onStopUpdate: (stop: Partial<IStop>) => void
  sequence: number
  loading?: boolean
  error?: string
}

export const InstructionInlineEdit = memo<IInstructionInlineEdit>(
  ({
    currentEditField,
    error,
    loading,
    onCancel,
    onEditModeRequested,
    onStopUpdate,
    onSubmit,
    sequence,
    stop,
  }) => {
    const { id, instructions = '', stopSequence, stopType } = stop || {}
    const testString = `${stopType}-${sequence}`
    const NAME = `stop-instruction-${id}`
    const isEditMode = currentEditField === NAME

    const [debouncedInstructions, setDebouncedInstructions] = useState<string>(instructions)
    const [debouncedOnStopUpdate] = useDebouncedCallback(
      () => onStopUpdate({ instructions: debouncedInstructions }),
      300
    )

    // Prevent the inline edit section from closing if there is an error.
    useEffect(() => {
      if (!error && !loading && isEditMode) onEditModeRequested('')
    }, [error, loading, onEditModeRequested])

    const instructionsChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        setDebouncedInstructions(value)
        debouncedOnStopUpdate()
      },
      [debouncedOnStopUpdate]
    )

    return (
      <InlineEditContainer
        isLoading={loading}
        dataTest={`load-details-stop-instructions-${stopType}-${stopSequence}`}
        name={NAME}
        editModeEnabled={isEditMode}
        resetOnClickOutside={false}
        editMode={
          <Card className='pa4'>
            <TextArea
              size='large'
              dataTest={`stop-instructions-text-area-${stopType}-${stopSequence}`}
              label={`${capitalize(stopType)} instructions`}
              ariaLabel={`load-details-stop-instructions-${stopType}-${stopSequence}`}
              placeholder='Enter instructions for your driver'
              onChange={instructionsChangeHandler}
              value={debouncedInstructions}
            />
            <div className='tr'>
              <div className='mt4'>
                <Button color='tertiary' size='large' onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  data-test='stop-instructions-submit'
                  size='large'
                  className='ml2'
                  onClick={onSubmit}>
                  Save
                </Button>
              </div>
            </div>
          </Card>
        }
        onEditModeRequested={onEditModeRequested}>
        <InstructionDisplay
          dataTest={`${testString}-stop-instructions`}
          instructions={instructions}
        />
      </InlineEditContainer>
    )
  }
)
